import React, { memo, Suspense } from 'react';
import classNames from 'classnames';
import AppNavbar from '../Components/AppNavbar/AppNavbar';
import EDView from '../BaseComponents/View/EDView';
import BaseRouter from '../Features/Routing/BaseRouter/BaseRouter';
import EDMessages from '../BaseComponents/Messages/EDMessages';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import * as Highcharts from 'highcharts';
import ModelProvider from '../Model/ModelContext/ModelContext';
import { AvenyContextProvider } from './AvenyContext';
import KodplanManager from '../Components/KodplanManager/KodplanManager';
import { ActiveDatabaseProvider } from '../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import BrowserRecommendationDialog from '../Components/BrowserRecommendationDialog/BrowserRecommendationDialog';
import { IToggleOpen, MenuContext } from './MenuContext';
import { LoginStateProvider } from './Login/Contexts/LoginStateContext/LoginStateContext';
import { ISingleSignOnParam } from '../Components/SingleSignOn/SingleSignOn';
import ErrorBoundary from '../Components/ErrorBoundary/ErrorBoundary';
import 'moment/locale/sv';
import { RutinSettingsContextProvider } from './Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import KonteringManager from './Delsystem/_Common/Components/KonteringManager/KonteringManager';
import ErrorDialogManager from '../BaseComponents/ErrorDialogManager/ErrorDialogManager';
import DatePickersLocalizationProvider from './DatePickersLocalizationProvider/DatePickersLocalizationProvider';
import { AppContainer } from '../Components/AppContainer/AppContainer';
import BreadcrumbsWrapper from './Delsystem/_Common/Components/BreadcrumbsWrapper/BreadcrumbsWrapper';
import { FloatingMenuPresenceProvider } from '../Components/FloatingBottomArea/FloatingMenuPresenceContext';
import { FloatingBottomAreaSysoptionProvider } from '../Components/FloatingBottomArea/FloatingBottomAreaSysoptionContext';
import { AppBarScrollContextProvider } from '../Components/AppNavbar/AppBarScrollContext';

interface IProps {
  atLogin: boolean;
  atStart: boolean;
  classes?: any;
}

interface IState {
  leftMenuOpen: boolean;
  rightMenuOpen: boolean;
  singleRoutineMode: {
    enabled: boolean;
    rutinId: number | null;
  };
  didLoginViaSingleSignOn: boolean;
  formEditMode: boolean;
  singleSignOnParams: ISingleSignOnParam[];
  formHasChanged: boolean;
  openChoiceDialog: boolean;
  openChoiceCallback: (() => void) | undefined;
}

const styles = (theme: Theme) => ({
  rightMenuMargin: {
    marginRight: 320,
  },
  menuOpen: {
    transition: theme.transitions?.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuClose: {
    transition: theme.transitions?.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
});

/**
Grundkomponent för programmet.
*/
class Aveny extends React.PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    Highcharts.setOptions({
      lang: {
        decimalPoint: ',',
      },
    });

    this.state = {
      leftMenuOpen: false,
      rightMenuOpen: false,
      singleRoutineMode: {
        enabled: window.env.singleRoutineMode,
        rutinId: 0,
      },
      didLoginViaSingleSignOn: false,
      formEditMode: false,
      formHasChanged: false,
      singleSignOnParams: [],
      openChoiceDialog: false,
      openChoiceCallback: undefined,
    };
  }

  onMenuToggle: IToggleOpen = (
    anchor: 'left' | 'right',
    value: boolean,
    callback?: () => void
  ) => {
    switch (anchor) {
      case 'left':
        this.setState(
          {
            leftMenuOpen: value,
          },
          callback
        );
        break;
      case 'right':
        this.setState(
          {
            rightMenuOpen: value,
          },
          callback
        );
        break;
    }
  };

  setDidLoginViaSingleSignOn = (newValue: boolean) => {
    this.setState({ didLoginViaSingleSignOn: newValue });
  };

  setSingleSignOnParams = (params: ISingleSignOnParam[]) => {
    this.setState({ singleSignOnParams: params });
  };

  setFormEditMode = (value: boolean, localToggle: () => void) => {
    this.setState(
      {
        formEditMode: value,
      },
      localToggle
    );
  };

  setFormHasChanged = (value: boolean) => {
    if (this.state.formHasChanged !== value) {
      this.setState({
        formHasChanged: value,
      });
    }
  };

  setOpenChoiceDialog = (value: boolean, openChoiceCallback?: () => void) => {
    if (openChoiceCallback == null) {
      this.setState({
        openChoiceDialog: value,
      });
    } else {
      this.setState({
        openChoiceDialog: value,
        openChoiceCallback,
      });
    }
  };

  render() {
    return (
      <ErrorBoundary name="Aveny">
        {/* Här är en sista chans att fånga ett fel och visa felmeddelandet. Men
        hela programmet har krashat så om vi får fel i den här errorboundarien
        här kan vi fundera på om det felet hade gått att hantera tidigare. */}
        <DatePickersLocalizationProvider>
          <LoginStateProvider
            atLoginOrStart={this.props.atLogin || this.props.atStart}
          >
            {routeIsAtLogin => {
              return (
                <ActiveDatabaseProvider>
                  <FloatingBottomAreaSysoptionProvider>
                    <KodplanManager>
                      <ModelProvider>
                        <AppBarScrollContextProvider>
                          <AppContainer>
                            <EDMessages>
                              <ErrorDialogManager>
                                <MenuContext.Provider
                                  value={{
                                    leftMenuOpen: this.state.leftMenuOpen,
                                    rightMenuOpen: this.state.rightMenuOpen,
                                    toggleOpen: this.onMenuToggle,
                                  }}
                                >
                                  <AvenyContextProvider
                                    value={{
                                      singleRoutineMode: {
                                        enabled:
                                          this.state.singleRoutineMode.enabled,
                                        rutinId:
                                          this.state.singleRoutineMode.rutinId,
                                      },
                                      didLoginViaSingleSignOn:
                                        this.state.didLoginViaSingleSignOn,
                                      setDidLoginViaSingleSignOn:
                                        this.setDidLoginViaSingleSignOn,
                                      formHasChanged: this.state.formHasChanged,
                                      setFormHasChanged: this.setFormHasChanged,
                                      singleSignOnParams:
                                        this.state.singleSignOnParams,
                                      setSingleSignOnParams:
                                        this.setSingleSignOnParams,
                                      openChoiceDialog:
                                        this.state.openChoiceDialog,
                                      setOpenChoiceDialog:
                                        this.setOpenChoiceDialog,
                                      openChoiceCallback:
                                        this.state.openChoiceCallback,
                                    }}
                                  >
                                    <KonteringManager>
                                      <RutinSettingsContextProvider>
                                        <BreadcrumbsWrapper>
                                          <FloatingMenuPresenceProvider>
                                            <AppNavbar />
                                            <EDView
                                              style={{
                                                display: 'flex',
                                                flex: '1 1 auto',
                                                overflowY: 'hidden',
                                                flexDirection: 'column',
                                              }}
                                              className={classNames({
                                                [this.props.classes
                                                  .rightMenuMargin]:
                                                  this.state.rightMenuOpen,
                                                [this.props.classes.menuOpen]:
                                                  this.state.leftMenuOpen ||
                                                  this.state.rightMenuOpen,
                                                [this.props.classes.menuOpen]:
                                                  this.state.leftMenuOpen ||
                                                  this.state.rightMenuOpen,
                                                [this.props.classes.menuClose]:
                                                  !this.state.leftMenuOpen &&
                                                  !this.state.rightMenuOpen,
                                              })}
                                            >
                                              <ErrorBoundary name="BaseRouter">
                                                {/* fel bör hanteras tidigare än här. */}
                                                <Suspense fallback={null}>
                                                  {/* Suspense för rutiner som behöver laddas ner innan visning, suspense för mer specifika saker bör ske tidigare */}
                                                  <BaseRouter />
                                                </Suspense>
                                              </ErrorBoundary>
                                            </EDView>
                                          </FloatingMenuPresenceProvider>
                                        </BreadcrumbsWrapper>
                                      </RutinSettingsContextProvider>
                                    </KonteringManager>
                                  </AvenyContextProvider>
                                </MenuContext.Provider>
                              </ErrorDialogManager>
                            </EDMessages>
                            <BrowserRecommendationDialog />
                          </AppContainer>
                        </AppBarScrollContextProvider>
                      </ModelProvider>
                    </KodplanManager>
                  </FloatingBottomAreaSysoptionProvider>
                </ActiveDatabaseProvider>
              );
            }}
          </LoginStateProvider>
        </DatePickersLocalizationProvider>
      </ErrorBoundary>
    );
  }
}

export default memo(withStyles(Aveny, styles));
